import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date-picker";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api, apiErrorMessage } from "@/lib/api";
import { formatNumber } from "@/lib/i18n";
import { supplyCodeField } from "@/lib/supply-code";
import { useApiMutation } from "@/lib/use-api-mutation";
import { useApiQuery } from "@/lib/use-api-query";
import { cn, formatCurrency } from "@/lib/utils";
import { Link, createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/accounting/inbox` — documents nobody typed in.
 *
 * Two ways in: photographed on a phone, or forwarded to Daniela's
 * mailbox. Either way the file is on the left and on the right a form
 * pre-filled with what the model read from it. Until the accountant
 * presses *Create document*, nothing is in the books — which is why
 * the whole page is built as confirming a proposal, not as a list of
 * finished documents.
 */

type Extraction = {
  summary: string | null;
  unreadable: string[];
  paymentMethod?: string | null;
  /** Present when the document carried its own data (ISDOC). */
  lines?: Array<{
    description: string;
    quantity: string;
    unitName: string | null;
    unitPrice: string;
    vatRate: string;
  }>;
  supplierCountry?: string | null;
} | null;

/** Every field is optional and explicitly `| undefined`: the form is
 *  built by patching, and `exactOptionalPropertyTypes` treats "absent"
 *  and "set to undefined" as different things. */
type DraftLine = {
  description: string;
  quantity: string;
  unitName: string;
  unitPrice: string;
  vatRate: string;
};

type Draft = {
  supplierName?: string | undefined;
  contactId?: string | undefined;
  kind?: "invoice" | "receipt" | "other" | "credit_note" | undefined;
  /** On a credit note: the invoice it corrects. */
  correctsNumber?: string | null | undefined;
  supplyType?:
    | "service"
    | "goods"
    | "goods_with_installation"
    | "construction"
    | "import"
    | "other"
    | undefined;
  domesticReverseCharge?: boolean | undefined;
  vatSupplyCode?: string | undefined;
  vatDeductible?: boolean | undefined;
  supplierCountry?: string | undefined;
  /** `false` when the document says the supplier is not a VAT payer. */
  supplierIsVatPayer?: boolean | undefined;
  vatMode?: "standard" | "reverse_charge" | undefined;
  exchangeRate?: string | undefined;
  receivedOn?: string | undefined;
  lines?: DraftLine[] | undefined;
  supplierRegistrationNo?: string | undefined;
  supplierVatNo?: string | undefined;
  number?: string | undefined;
  variableSymbol?: string | undefined;
  issuedOn?: string | undefined;
  taxableOn?: string | undefined;
  dueOn?: string | undefined;
  currency?: string | undefined;
  subtotal?: string | undefined;
  vatTotal?: string | undefined;
  total?: string | undefined;
  alreadyPaid?: boolean | undefined;
};

type ContactOption = {
  id: string;
  name: string;
  role: "customer" | "supplier" | "both";
  registrationNo: string | null;
  vatNo: string | null;
  country: string;
  defaultDueDays: number | null;
  currency: string;
};

type Match = {
  id: string;
  name: string;
  registrationNo: string | null;
  vatNo: string | null;
  country: string;
  defaultDueDays: number | null;
  currency: string;
};

type PaymentCandidate = {
  transactionId: string;
  bookedOn: string;
  amount: string;
  currency: string;
  card: { merchant: string; amount: string; currency: string } | null;
  payee: string | null;
  reasons: Array<
    "variable_symbol" | "amount" | "card_amount" | "supplier_name" | "date" | "together"
  >;
  parts: Array<{
    transactionId: string;
    bookedOn: string;
    amount: string;
    message: string | null;
  }> | null;
};

/**
 * The payment to preselect, or none. A name-and-day match is only offered.
 * Of equally good ones the nearest to the invoice date wins, or either of two
 * that are the same charge twice.
 */
function preselectPayment(
  payments: PaymentCandidate[],
  issuedOn: string | null | undefined,
): string | null {
  const strong = payments.filter((p) => !p.reasons.includes("date"));
  const [first, second] = strong;
  if (!first) return null;
  if (!second || first.reasons.length > second.reasons.length) return first.transactionId;
  const issued = issuedOn ? new Date(issuedOn).getTime() : null;
  if (first.bookedOn === second.bookedOn && first.amount === second.amount)
    return first.transactionId;
  if (issued == null) return null;
  const gap = (p: PaymentCandidate) => Math.abs(new Date(p.bookedOn).getTime() - issued);
  return gap(second) - gap(first) >= 86_400_000 ? first.transactionId : null;
}

function paymentBody(payments: PaymentCandidate[], id: string | null) {
  const parts = payments.find((p) => p.transactionId === id)?.parts;
  return parts ? { transactionIds: parts.map((p) => p.transactionId) } : { transactionId: id };
}

type Duplicate = {
  kind: "expense" | "scan";
  id: string;
  label: string;
  reason: "same_number" | "same_amount_and_day";
};

type Scan = {
  id: string;
  status: "pending" | "extracted" | "failed" | "filed" | "discarded";
  source: "upload" | "email" | "fakturoid";
  senderEmail: string | null;
  senderUnverified: string | null;
  subject: string | null;
  mimeType: string;
  note: string | null;
  capturedAt: string | null;
  createdAt: string;
  extractionError: string | null;
  extraction: Extraction;
  /** The model that read it, or `isdoc` when the document carried its
   *  own data and nothing was inferred. */
  extractionModel: string | null;
  draft: Draft;
  /** Who the address book thinks it is from. A proposal like the rest
   *  of the extraction — nothing is filled in from it silently. */
  match: Match | null;
  /** The archived file is no longer where the row says it is. */
  fileMissing: boolean;
  /** The same supplier document already in the books or in this queue. */
  duplicates: Duplicate[];
  /** The imported expense this reads again from its original; filing cancels it. */
  replaces?: {
    id: string;
    number: string | null;
    internalNumber: string | null;
    partnerName: string;
    total: string;
    currency: string;
  } | null;
  /** Bank movements that would pay it, best first. */
  payments: PaymentCandidate[];
  /** What doesn't add up in the reading (own VAT number, sums, customer, OSS tax). */
  warnings: string[];
  uploadedBy: { id: string; name: string } | null;
};

/**
 * The model answers in its tool's field names; a person reading the
 * queue should not have to. An unknown name is shown as it came rather
 * than dropped — a field we stopped translating is still information.
 */
function fieldLabel(t: TFunction, name: string): string {
  return t(`accInbox.fields.${name}`, { defaultValue: name.replace(/_/g, " ") });
}

const INPUT =
  "h-9 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

type Mailbox = {
  configured: boolean;
  address: string;
  openToAnySender: boolean;
  lastRun: {
    status: "pending" | "running" | "completed" | "failed";
    finishedAt: string | null;
    error: string | null;
    result: {
      messages?: number;
      accepted?: number;
      skipped?: Array<{ from: string; subject: string; reason: string }>;
    } | null;
  } | null;
};

type FakturoidBox = {
  disconnected?: boolean;
  lastRun: {
    status: string;
    finishedAt: string | null;
    error: string | null;
    result: {
      files?: number;
      added?: number;
      skipped?: number;
      failed?: number;
      logs?: string[];
    } | null;
  } | null;
};

export const Route = createFileRoute("/_app/accounting/inbox")({
  loader: async () => {
    try {
      const [queue, mailbox, box, contacts, discarded] = await Promise.all([
        api.get<{ scans: Scan[]; total: number }>("/api/accounting/inbox?pageSize=50"),
        api.get<Mailbox>("/api/accounting/inbox/mailbox"),
        api.get<FakturoidBox>("/api/accounting/inbox/fakturoid"),
        api.get<{ contacts: ContactOption[] }>("/api/accounting/contacts"),
        api.get<{ scans: Scan[] }>("/api/accounting/inbox?status=discarded&pageSize=50"),
      ]);
      const duplicates = discarded.scans.filter((d) =>
        (d as Scan & { discardReason?: string | null }).discardReason?.startsWith("Duplicate"),
      );
      return { ...queue, mailbox, box, contacts: contacts.contacts, duplicates };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: InboxPage,
});

function InboxPage() {
  const { scans, total, mailbox, box, contacts, duplicates } = Route.useLoaderData();
  const router = useRouter();
  const { t } = useTranslation();

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-semibold text-2xl">{t("accInbox.title")}</h1>
        <p className="text-muted-foreground text-sm">{t("accInbox.subtitle")}</p>
      </div>

      <DropBox onDone={() => router.invalidate()} />

      <RereadAll count={scans.length} onDone={() => router.invalidate()} />

      <SetAsideDuplicates scans={duplicates} onDone={() => router.invalidate()} />

      <MailboxPanel mailbox={mailbox} onDone={() => router.invalidate()} />

      {!box.disconnected && <FakturoidBoxPanel box={box} onDone={() => router.invalidate()} />}

      {scans.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground text-sm">
            <Trans
              i18nKey="accInbox.queueEmpty"
              components={{ scanner: <Link to="/download/scanner" className="underline" /> }}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-muted-foreground text-sm">{t("accInbox.inQueue", { total })}</p>
          {scans.map((scan) => (
            <ScanCard
              key={scan.id}
              scan={scan}
              contacts={contacts}
              onDone={() => router.invalidate()}
            />
          ))}
        </>
      )}
    </div>
  );
}

/**
 * The third way in: a file from this computer.
 *
 * The phone has a camera and the mailbox has forwarded mail; a PDF
 * already sitting on a disk had no door until now, and printing it to
 * photograph it is not a workflow.
 */
function DropBox({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const body = new FormData();
        body.set("file", file);
        const res = await fetch("/api/accounting/inbox", {
          method: "POST",
          body,
          credentials: "same-origin",
        });
        if (!res.ok) {
          const detail = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(detail?.error ?? t("accInbox.dropbox.refused", { name: file.name }));
        }
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("accInbox.dropbox.failed"));
    } finally {
      setBusy(false);
      setOver(false);
    }
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        void send(e.dataTransfer.files);
      }}
      className={cn(
        "rounded-lg border border-dashed p-6 text-center text-sm transition-colors",
        over ? "border-primary bg-primary/5" : "border-border",
      )}
    >
      <label className="cursor-pointer">
        <span className="font-medium underline">
          {busy ? t("accInbox.dropbox.uploading") : t("accInbox.dropbox.upload")}
        </span>
        <input
          type="file"
          multiple
          accept="application/pdf,image/*"
          className="hidden"
          disabled={busy}
          onChange={(e) => void send(e.target.files)}
        />
      </label>
      <p className="mt-1 text-muted-foreground text-xs">{t("accInbox.dropbox.hint")}</p>
      {error && <p className="mt-2 text-destructive text-xs">{error}</p>}
    </div>
  );
}

/**
 * The mailbox panel. It exists to answer the two questions people ask
 * about an address they forward to: is it working, and did my e-mail
 * arrive? The second one is why the refusals are listed — an invoice
 * that was turned away for an unknown sender otherwise just never
 * shows up.
 */
function MailboxPanel({ mailbox, onDone }: { mailbox: Mailbox; onDone: () => void }) {
  const { t } = useTranslation();
  const check = useApiMutation(() => api.post("/api/accounting/inbox/mailbox/run", {}), {
    onSuccess: onDone,
    fallbackMessage: t("accInbox.mailbox.queueFailed"),
  });
  const skipped = mailbox.lastRun?.result?.skipped ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">{t("accInbox.mailbox.title")}</CardTitle>
          <CardDescription>
            <Trans
              i18nKey="accInbox.mailbox.description"
              values={{ address: mailbox.address }}
              components={{ address: <span className="font-mono" /> }}
            />
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={mailbox.configured ? "success" : "secondary"}>
            {mailbox.configured ? t("accInbox.mailbox.watching") : t("accInbox.mailbox.notSetUp")}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            disabled={!mailbox.configured || check.pending}
            onClick={() => check.mutate()}
          >
            {check.pending ? t("accInbox.mailbox.queueing") : t("accInbox.mailbox.checkNow")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {!mailbox.configured && (
          <p className="text-muted-foreground">
            <Trans i18nKey="accInbox.mailbox.noPassword" components={{ code: <code /> }} />
          </p>
        )}
        {mailbox.lastRun && (
          <p className="text-muted-foreground">
            {t("accInbox.mailbox.lastRead")}{" "}
            {mailbox.lastRun.finishedAt ? (
              <LocalDate value={mailbox.lastRun.finishedAt} withTime />
            ) : (
              t("accInbox.mailbox.isRunning")
            )}
            {t("accInbox.mailbox.lastReadCounts", {
              messages: mailbox.lastRun.result?.messages ?? 0,
              accepted: mailbox.lastRun.result?.accepted ?? 0,
            })}
          </p>
        )}
        {mailbox.lastRun?.error && <p className="text-destructive">{mailbox.lastRun.error}</p>}
        {skipped.length > 0 && (
          <div>
            <p className="text-amber-600">{t("accInbox.mailbox.notTaken")}</p>
            <ul className="mt-1 space-y-0.5 text-muted-foreground text-xs">
              {skipped.map((item) => (
                <li key={`${item.from}-${item.subject}`}>
                  {item.from || t("accInbox.mailbox.noSender")} — {item.subject}: {item.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!mailbox.openToAnySender && (
          <p className="text-muted-foreground text-xs">{t("accInbox.mailbox.restricted")}</p>
        )}
      </CardContent>
    </Card>
  );
}

/** Fakturoid's document box: files dropped there come into this queue. */
function FakturoidBoxPanel({ box, onDone }: { box: FakturoidBox; onDone: () => void }) {
  const { t } = useTranslation();
  const pull = useApiMutation(() => api.post("/api/accounting/inbox/fakturoid/run", {}), {
    onSuccess: onDone,
    fallbackMessage: t("accInbox.fakturoid.queueFailed"),
  });
  const run = box.lastRun;
  const problems = (run?.result?.logs ?? []).filter((line) => !line.includes('{"files"'));

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">{t("accInbox.fakturoid.title")}</CardTitle>
          <CardDescription>{t("accInbox.fakturoid.description")}</CardDescription>
        </div>
        <Button variant="outline" size="sm" disabled={pull.pending} onClick={() => pull.mutate()}>
          {pull.pending ? t("accInbox.fakturoid.queueing") : t("accInbox.fakturoid.pullNow")}
        </Button>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {!run ? (
          <p className="text-muted-foreground">{t("accInbox.fakturoid.noPull")}</p>
        ) : (
          <p className="text-muted-foreground">
            {t("accInbox.fakturoid.lastPull")}{" "}
            {run.finishedAt ? (
              <LocalDate value={run.finishedAt} withTime />
            ) : (
              t("accInbox.fakturoid.isRunning")
            )}
            {t("accInbox.fakturoid.counts", {
              files: run.result?.files ?? 0,
              added: run.result?.added ?? 0,
            })}
          </p>
        )}
        {run?.error && <p className="text-destructive">{run.error}</p>}
        {problems.length > 0 && (
          <ul className="space-y-0.5 text-amber-700 text-xs dark:text-amber-400">
            {problems.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
        {pull.error && <p className="text-destructive">{pull.error}</p>}
      </CardContent>
    </Card>
  );
}

/** The value the picker uses for "this supplier isn't in the book". */
const NOT_IN_BOOK = "__not_in_book__";

function ScanCard({
  scan,
  contacts,
  onDone,
}: {
  scan: Scan;
  contacts: ContactOption[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Draft>(() => ({
    ...(scan.draft ?? {}),
    // The draft's dates arrive as ISO instants (the server builds real
    // `Date`s from what the model read). A `<input type="date">` takes
    // `YYYY-MM-DD` and silently shows nothing for anything else — which
    // looked like the model had failed to read a date it had read fine.
    ...dateFields(scan.draft),
    // A document that came with its own data needs no guessing: its
    // lines and the supplier's country are facts, not readings.
    ...(scan.extraction?.lines?.length
      ? {
          lines: scan.extraction.lines.map((line) => ({
            description: line.description,
            quantity: line.quantity,
            unitName: line.unitName ?? "",
            unitPrice: line.unitPrice,
            vatRate: line.vatRate,
          })),
        }
      : {}),
    ...(scan.extraction?.supplierCountry
      ? { supplierCountry: scan.extraction.supplierCountry }
      : {}),
    // The address book's match is offered, not applied: the identity
    // below fills from it, and a glance at the picker says where the
    // registration number came from.
    ...(scan.match
      ? {
          contactId: scan.match.id,
          supplierName: scan.match.name,
          supplierRegistrationNo: scan.match.registrationNo ?? undefined,
          supplierVatNo: scan.match.vatNo ?? undefined,
          supplierCountry: scan.match.country,
        }
      : {}),
  }));
  const [reason, setReason] = useState("");
  const [fileAnyway, setFileAnyway] = useState(false);
  // One candidate, or a clear best one, is preselected; the person confirms or changes it.
  const [paymentId, setPaymentId] = useState<string | null>(() =>
    preselectPayment(scan.payments, scan.draft.issuedOn),
  );
  const [lateDuplicates, setLateDuplicates] = useState<Duplicate[] | null>(null);
  const duplicates = lateDuplicates ?? scan.duplicates;

  const file = useApiMutation(
    (body: Record<string, unknown>) =>
      api.post(`/api/accounting/inbox/${scan.id}/file`, {
        ...body,
        confirmDuplicate: fileAnyway,
        ...paymentBody(scan.payments, paymentId),
      }),
    {
      onSuccess: onDone,
      errorMessage: (err) => {
        // The corrected form can match something the model's reading didn't.
        if (err instanceof ApiError && err.status === 409) {
          const detail = err.detail as { duplicates?: Duplicate[] } | undefined;
          if (detail?.duplicates) setLateDuplicates(detail.duplicates);
          return t("accInbox.scan.duplicateConflict");
        }
        return apiErrorMessage(err, t("accInbox.scan.createFailed"));
      },
    },
  );
  const discard = useApiMutation(
    (why: string) => api.post(`/api/accounting/inbox/${scan.id}/discard`, { reason: why }),
    { onSuccess: onDone, errorMessage: () => t("accInbox.scan.discardFailed") },
  );
  const reread = useApiMutation(() => api.post(`/api/accounting/inbox/${scan.id}/reread`, {}), {
    onSuccess: onDone,
    errorMessage: () => t("accInbox.scan.rereadFailed"),
  });

  const set = (key: keyof Draft) => (value: string) => setForm({ ...form, [key]: value });

  // Who accounts for the tax takes both halves: where the supplier is,
  // AND whether they charged Czech tax. A foreign supplier registered
  // here (or selling under the one-stop shop) charges it and accounts
  // for it themselves — self-assessing on top would put the same tax
  // on the return twice.
  const foreign = isForeign(form.supplierCountry, form.supplierVatNo);
  const chargedCzechVat = documentCarriesVat(form);
  const reverseCharge =
    form.vatMode === "reverse_charge" ||
    (form.vatMode === undefined && foreign && !chargedCzechVat);

  const lines = form.lines ?? [];
  const totals = totalsOf(lines, reverseCharge);
  // Lines win when there are any: they are what the document says, and
  // the totals below are then arithmetic, not a second opinion.
  const subtotal = lines.length > 0 ? totals.subtotal : Number(form.subtotal ?? 0);
  const vatTotal = lines.length > 0 ? totals.vat : Number(form.vatTotal ?? 0);
  const total = lines.length > 0 ? totals.total : Number(form.total ?? 0);

  const rate = useExchangeRate(form.currency, form.taxableOn ?? form.issuedOn);
  const { verdict, overridden } = useVatVerdict(form, form.vatMode);
  const supplyCodes = useApiQuery(
    () => api.get<Record<string, string>>("/api/accounting/vat/supply-codes"),
    [],
  );
  // What is submitted: the person's choice, else the rules', and a credit note follows its invoice on the server.
  const effectiveMode =
    form.kind === "credit_note"
      ? undefined
      : (form.vatMode ?? verdict?.mode ?? (reverseCharge ? "reverse_charge" : "standard"));
  const supplyCode = supplyCodeField(
    { ...form, domestic: !foreign, vatMode: effectiveMode },
    supplyCodes.data,
  );

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (supplyCode.missing) return;
    file.mutate({
      contactId: form.contactId ?? null,
      supplierName: form.supplierName ?? "",
      supplierRegistrationNo: form.supplierRegistrationNo ?? null,
      supplierVatNo: form.supplierVatNo ?? null,
      supplierCountry: form.supplierCountry ?? null,
      supplierIsVatPayer: form.supplierIsVatPayer,
      kind: form.kind ?? "invoice",
      // The accountant's choice if they made one, the rules' otherwise.
      // A credit note's regime follows the invoice it corrects; the server decides.
      vatMode: effectiveMode,
      correctsNumber: form.kind === "credit_note" ? (form.correctsNumber ?? null) : null,
      supplyType: form.supplyType ?? "service",
      domesticReverseCharge: form.domesticReverseCharge === true,
      ...(supplyCode.submitted ? { vatSupplyCode: supplyCode.submitted } : {}),
      vatDeductible: form.vatDeductible !== false,
      exchangeRate: Number(form.exchangeRate ?? rate.value ?? 1),
      number: form.number ?? null,
      variableSymbol: form.variableSymbol ?? null,
      issuedOn: form.issuedOn,
      taxableOn: form.taxableOn ?? null,
      dueOn: form.dueOn ?? form.issuedOn,
      receivedOn: form.receivedOn ?? null,
      currency: form.currency ?? "CZK",
      subtotal,
      vatTotal,
      total,
      lines: lines.map((line) => ({
        description: line.description,
        quantity: Number(line.quantity || 1),
        unitName: line.unitName || null,
        unitPrice: Number(line.unitPrice || 0),
        vatRate: Number(line.vatRate || 0),
      })),
      alreadyPaid: form.alreadyPaid === true,
    });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">
            {scan.extraction?.summary ?? t("accInbox.scan.untitled")}
          </CardTitle>
          <CardDescription>
            {scan.source === "email" ? (
              <>
                {t("accInbox.scan.byEmail", {
                  sender: scan.senderEmail ?? t("accInbox.scan.unknown"),
                })}
                {scan.subject && t("accInbox.scan.withSubject", { subject: scan.subject })}
              </>
            ) : scan.source === "fakturoid" ? (
              <>
                {t("accInbox.scan.fromFakturoid")}
                {scan.note && t("accInbox.scan.withNote", { note: scan.note })}
              </>
            ) : (
              (scan.uploadedBy?.name ?? t("accInbox.scan.unknown"))
            )}{" "}
            · <LocalDate value={scan.capturedAt ?? scan.createdAt} withTime />
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {scan.senderUnverified && (
            <Badge
              variant="destructive"
              title={t("accInbox.scan.senderUnverifiedHint", { reason: scan.senderUnverified })}
              data-testid="sender-unverified"
            >
              {t("accInbox.scan.senderUnverified")}
            </Badge>
          )}
          {scan.status === "pending" && (
            <Badge variant="secondary">{t("accInbox.scan.reading")}</Badge>
          )}
          {scan.status === "failed" && (
            <Badge variant="destructive">{t("accInbox.scan.unreadable")}</Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={reread.pending}
            onClick={() => reread.mutate()}
          >
            {t("accInbox.scan.readAgain")}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          {scan.fileMissing ? (
            <MissingFile scan={scan} onDone={onDone} />
          ) : scan.mimeType === "application/pdf" ? (
            // A PDF is not an image; an <img> would render a broken
            // icon and hide the one thing being confirmed against.
            <object
              data={`/api/accounting/inbox/${scan.id}/photo`}
              type="application/pdf"
              className="h-[32rem] w-full rounded-lg border"
              aria-label={t("accInbox.scan.document")}
            >
              <a
                className="underline"
                href={`/api/accounting/inbox/${scan.id}/photo`}
                target="_blank"
                rel="noreferrer"
              >
                {t("accInbox.scan.openPdf")}
              </a>
            </object>
          ) : (
            <img
              src={`/api/accounting/inbox/${scan.id}/photo`}
              alt={t("accInbox.scan.document")}
              className="w-full rounded-lg border object-contain"
            />
          )}
          {scan.extractionError && (
            <p className="text-destructive text-xs">{scan.extractionError}</p>
          )}
          {scan.extraction?.unreadable && scan.extraction.unreadable.length > 0 && (
            // What the model couldn't read should be seen before the
            // accountant hunts for it in empty fields.
            <p className="text-amber-600 text-xs">
              {t("accInbox.scan.unreadableFields")}{" "}
              {scan.extraction.unreadable.map((name) => fieldLabel(t, name)).join(", ")}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-wrap items-center gap-1">
            <span className="mr-2 text-muted-foreground text-xs">
              {t("accInbox.scan.documentType")}
            </span>
            {(["invoice", "receipt", "credit_note", "other"] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => setForm((current) => ({ ...current, kind }))}
                className={cn(
                  "rounded-md border px-3 py-1 text-sm capitalize",
                  (form.kind ?? "invoice") === kind
                    ? "border-primary bg-primary/10 text-primary"
                    : "text-muted-foreground",
                )}
              >
                {t(`accInbox.scan.kind.${kind}`)}
              </button>
            ))}
          </div>

          {form.kind === "credit_note" && (
            <div className="space-y-1">
              <Field
                label={t("accInbox.scan.correctsNo")}
                value={form.correctsNumber ?? undefined}
                onChange={set("correctsNumber")}
              />
              <p className="text-muted-foreground text-xs">{t("accInbox.scan.creditNoteHint")}</p>
            </div>
          )}

          <SupplierPicker
            contacts={contacts}
            form={form}
            read={scan.draft.supplierName ?? null}
            onPick={(patch) => setForm((current) => ({ ...current, ...patch }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t("accInbox.scan.registrationNo")}
              value={form.supplierRegistrationNo}
              onChange={set("supplierRegistrationNo")}
            />
            <Field
              label={t("accInbox.scan.vatNo")}
              value={form.supplierVatNo}
              onChange={set("supplierVatNo")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t("accInbox.scan.documentNumber")}
              value={form.number}
              onChange={set("number")}
            />
            <Field
              label={t("accInbox.scan.variableSymbol")}
              value={form.variableSymbol}
              onChange={set("variableSymbol")}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field
              label={t("accInbox.scan.issued")}
              type="date"
              required
              value={form.issuedOn}
              onChange={set("issuedOn")}
            />
            <Field
              label={t("accInbox.scan.taxableSupply")}
              type="date"
              value={form.taxableOn}
              onChange={set("taxableOn")}
            />
            <Field
              label={t("accInbox.scan.due")}
              type="date"
              value={form.dueOn}
              onChange={set("dueOn")}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field
              label={t("accInbox.scan.received")}
              type="date"
              value={form.receivedOn}
              onChange={set("receivedOn")}
            />
            <Field
              label={t("accInbox.scan.currency")}
              value={form.currency ?? "CZK"}
              onChange={set("currency")}
            />
            <Field
              label={t("accInbox.scan.rateToCzk")}
              type="number"
              value={form.exchangeRate ?? (rate.value != null ? String(rate.value) : undefined)}
              onChange={set("exchangeRate")}
            />
          </div>
          {(form.currency ?? "CZK").toUpperCase() !== "CZK" && (
            <p className="text-muted-foreground text-xs">
              {rate.state === "loading"
                ? t("accInbox.scan.rateLoading")
                : rate.state === "missing"
                  ? t("accInbox.scan.rateMissing")
                  : t("accInbox.scan.rateFound", {
                      rate:
                        rate.value != null
                          ? formatNumber(Number(rate.value), undefined, {
                              maximumFractionDigits: 6,
                            })
                          : "—",
                    })}
            </p>
          )}

          {form.kind !== "credit_note" && (
            <VatPanel
              form={form}
              verdict={verdict}
              overridden={overridden}
              reverseCharge={reverseCharge}
              supplyCode={supplyCode}
              supplyCodes={supplyCodes.data}
              onPick={(patch) => setForm((current) => ({ ...current, ...patch }))}
            />
          )}

          <LineEditor
            lines={lines}
            reverseCharge={reverseCharge}
            currency={form.currency ?? "CZK"}
            onChange={(next) => setForm((current) => ({ ...current, lines: next }))}
            onSeed={() =>
              setForm((current) => ({
                ...current,
                lines: [
                  {
                    description: scan.extraction?.summary ?? t("accInbox.scan.seedDescription"),
                    quantity: "1",
                    unitName: "",
                    unitPrice: current.subtotal ?? current.total ?? "0",
                    vatRate: current.vatTotal && current.subtotal ? "21" : "0",
                  },
                ],
              }))
            }
          />

          {lines.length === 0 && (
            <div className="grid grid-cols-3 gap-3">
              <Field
                label={t("accInbox.scan.base")}
                type="number"
                value={form.subtotal}
                onChange={set("subtotal")}
              />
              <Field
                label={t("accInbox.scan.vat")}
                type="number"
                value={form.vatTotal}
                onChange={set("vatTotal")}
              />
              <Field
                label={t("accInbox.scan.total")}
                type="number"
                required
                value={form.total}
                onChange={set("total")}
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm" htmlFor={`paid-${scan.id}`}>
            <input
              id={`paid-${scan.id}`}
              type="checkbox"
              checked={form.alreadyPaid === true}
              onChange={(e) => setForm({ ...form, alreadyPaid: e.target.checked })}
              className="h-4 w-4"
            />
            {t("accInbox.scan.paidInCash")}
          </label>

          {scan.warnings.length > 0 && (
            <ul className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 text-sm dark:bg-amber-950/30 dark:text-amber-200">
              {scan.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}

          <div className="space-y-1 rounded-md border p-3 text-sm">
            <p className="font-medium">{t("accInbox.scan.paidFromBank")}</p>
            {scan.extraction?.paymentMethod === "prepaid" && scan.payments.length === 0 ? (
              <p className="text-muted-foreground text-xs">{t("accInbox.scan.prepaid")}</p>
            ) : scan.payments.length === 0 ? (
              <p className="text-muted-foreground text-xs">{t("accInbox.scan.noMovement")}</p>
            ) : (
              <ul className="space-y-1">
                {scan.payments.map((p) => (
                  <li key={p.transactionId}>
                    <label
                      className="flex flex-wrap items-center gap-2"
                      htmlFor={`pay-${scan.id}-${p.transactionId}`}
                    >
                      <input
                        id={`pay-${scan.id}-${p.transactionId}`}
                        type="radio"
                        name={`pay-${scan.id}`}
                        checked={paymentId === p.transactionId}
                        onChange={() => setPaymentId(p.transactionId)}
                      />
                      <LocalDate value={p.bookedOn} />
                      <span className="tabular-nums">
                        {formatNumber(Number(p.amount))} {p.currency}
                      </span>
                      {p.card ? (
                        <span className="text-muted-foreground text-xs">
                          {t("accInbox.scan.card")} · {p.card.merchant} ·{" "}
                          {formatNumber(Number(p.card.amount))} {p.card.currency}
                        </span>
                      ) : (
                        p.payee && <span className="text-muted-foreground text-xs">{p.payee}</span>
                      )}
                      {p.reasons.map((r) => (
                        <Badge key={r} variant="secondary" className="text-[10px]">
                          {t(`accInbox.paymentReason.${r}`)}
                        </Badge>
                      ))}
                    </label>
                    {p.parts && (
                      <ul className="text-muted-foreground ml-6 text-xs">
                        {p.parts.map((part) => (
                          <li key={part.transactionId}>
                            <LocalDate value={part.bookedOn} /> ·{" "}
                            {formatNumber(Number(part.amount))} {p.currency}
                            {part.message && ` · ${part.message}`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
                <li>
                  <label
                    className="flex items-center gap-2 text-xs"
                    htmlFor={`pay-${scan.id}-none`}
                  >
                    <input
                      id={`pay-${scan.id}-none`}
                      type="radio"
                      name={`pay-${scan.id}`}
                      checked={paymentId === null}
                      onChange={() => setPaymentId(null)}
                    />
                    {t("accInbox.scan.noneOfThese")}
                  </label>
                </li>
              </ul>
            )}
          </div>

          {scan.replaces && (
            <p className="rounded-md border border-sky-500/40 bg-sky-500/5 p-2 text-sm">
              <Trans
                i18nKey="accInbox.scan.replaces"
                values={{
                  number: scan.replaces.number ?? scan.replaces.internalNumber,
                  partner: scan.replaces.partnerName,
                  total: formatNumber(Number(scan.replaces.total)),
                  currency: scan.replaces.currency,
                }}
                components={{
                  originalDocument: (
                    <Link
                      to="/accounting/expenses/$id"
                      params={{ id: scan.replaces.id }}
                      className="underline"
                    />
                  ),
                }}
              />
            </p>
          )}
          {duplicates.length > 0 && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:bg-amber-950/30">
              <p className="font-medium text-amber-800 dark:text-amber-300">
                {t("accInbox.scan.mayBeHere")}
              </p>
              <ul className="space-y-1">
                {duplicates.map((d) => (
                  <li key={`${d.kind}-${d.id}`}>
                    {d.kind === "expense" ? (
                      <Link
                        className="underline"
                        to="/accounting/expenses/$id"
                        params={{ id: d.id }}
                      >
                        {d.label}
                      </Link>
                    ) : (
                      d.label
                    )}{" "}
                    <span className="text-muted-foreground">
                      (
                      {d.reason === "same_number"
                        ? t("accInbox.scan.sameNumber")
                        : t("accInbox.scan.sameAmountAndDay")}
                      )
                    </span>
                  </li>
                ))}
              </ul>
              <label className="flex items-center gap-2" htmlFor={`anyway-${scan.id}`}>
                <input
                  id={`anyway-${scan.id}`}
                  type="checkbox"
                  checked={fileAnyway}
                  onChange={(e) => setFileAnyway(e.target.checked)}
                  className="h-4 w-4"
                />
                {t("accInbox.scan.createAnyway")}
              </label>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="submit"
              disabled={
                file.pending || supplyCode.missing || (duplicates.length > 0 && !fileAnyway)
              }
            >
              {file.pending ? t("accInbox.scan.creating") : t("accInbox.scan.create")}
            </Button>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("accInbox.scan.discardReason")}
              className="h-9 w-40 rounded-md border border-input bg-background px-2 text-sm"
            />
            <Button
              type="button"
              variant="outline"
              disabled={reason.trim().length < 2 || discard.pending}
              onClick={() => discard.mutate(reason.trim())}
            >
              {t("accInbox.scan.discard")}
            </Button>
          </div>
          {(file.error || discard.error) && (
            <p className="text-destructive text-sm">{file.error ?? discard.error}</p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * Who the document is from.
 *
 * The address book first, free text second — because the registration
 * and VAT numbers are things the book knows and a receipt usually
 * doesn't print. Picking a contact fills the identity from it and ties
 * the filed document to that contact's page; "not in the address book"
 * leaves the name as the model read it and files the document without
 * a partner.
 */
/**
 * The file this row was made from is gone.
 *
 * For a document that arrived by e-mail that is recoverable: Hub marks
 * mail read, it never deletes it, so the original is still sitting in
 * the mailbox. For an upload it is not — the phone that sent it has
 * long since moved on.
 */
function MissingFile({ scan, onDone }: { scan: Scan; onDone: () => void }) {
  const { t } = useTranslation();
  // `fallbackMessage`, not `errorMessage`: a constant message throws the
  // server's reason away, and the reason is the whole point here —
  // "that message is no longer in the mailbox" and "the mailbox has no
  // password" need different things done about them.
  const refetch = useApiMutation(() => api.post(`/api/accounting/inbox/${scan.id}/refetch`, {}), {
    onSuccess: onDone,
    fallbackMessage: t("accInbox.missingFile.fetchFailed"),
  });

  return (
    <div className="space-y-2 rounded-lg border border-amber-300 border-dashed p-6 text-sm">
      <p className="font-medium text-amber-700">{t("accInbox.missingFile.title")}</p>
      {scan.source === "email" ? (
        <>
          <p className="text-muted-foreground">{t("accInbox.missingFile.email")}</p>
          <Button size="sm" disabled={refetch.pending} onClick={() => refetch.mutate()}>
            {refetch.pending ? t("accInbox.missingFile.fetching") : t("accInbox.missingFile.fetch")}
          </Button>
        </>
      ) : (
        <p className="text-muted-foreground">
          {scan.source === "fakturoid"
            ? t("accInbox.missingFile.fakturoid")
            : t("accInbox.missingFile.upload")}
        </p>
      )}
      {refetch.error && <p className="text-destructive">{refetch.error}</p>}
    </div>
  );
}

/** The date-only half of a draft, in the shape a date input accepts. */
function dateFields(draft: Draft | undefined): Partial<Draft> {
  const day = (value: string | undefined) => (value ? value.slice(0, 10) : undefined);
  return {
    ...(draft?.issuedOn ? { issuedOn: day(draft.issuedOn) } : {}),
    ...(draft?.taxableOn ? { taxableOn: day(draft.taxableOn) } : {}),
    ...(draft?.dueOn ? { dueOn: day(draft.dueOn) } : {}),
    ...(draft?.receivedOn ? { receivedOn: day(draft.receivedOn) } : {}),
  };
}

/** EU + the rest: anything that isn't Czech is ours to assess. Mirrors
 *  `services/accounting/vat/reverse-charge.ts`; the server decides, this
 *  only shows what it will decide. */
function isForeign(country: string | undefined, vatNo: string | undefined): boolean {
  const stated = (country ?? "").trim().toUpperCase();
  if (stated.length === 2) return stated !== "CZ";
  const prefix = (vatNo ?? "").trim().toUpperCase().slice(0, 2);
  return /^[A-Z]{2}$/.test(prefix) ? prefix !== "CZ" : false;
}

type Verdict = {
  mode: string;
  selfAssess: boolean;
  deductible: boolean;
  returnLines: { tax: string[]; deduction: string[] };
  controlSection: string | null;
  rule: string;
  law: string | null;
  reason: string;
  needsReview: boolean;
};

/**
 * What the rules make of the document as it stands. Asked again on
 * every change that can matter, because the answer is the thing being
 * confirmed — not a detail of it.
 */
function useVatVerdict(form: Draft, chosenMode: Draft["vatMode"]) {
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const facts = JSON.stringify({
    supplierCountry: form.supplierCountry ?? null,
    supplierIsVatPayer: form.supplierIsVatPayer,
    supplierVatNo: form.supplierVatNo ?? null,
    statedVat: Number(form.vatTotal ?? 0),
    total: Number(form.total ?? 0),
    supplyType: form.supplyType ?? "service",
    domesticReverseCharge: form.domesticReverseCharge === true,
  });

  useEffect(() => {
    let live = true;
    api
      .post<Verdict>("/api/accounting/vat/classify", JSON.parse(facts))
      .then((res) => live && setVerdict(res))
      .catch(() => live && setVerdict(null));
    return () => {
      live = false;
    };
  }, [facts]);

  // An explicit choice by the accountant wins over the rules, and the
  // panel says so rather than quietly showing a verdict that is no
  // longer what will be filed.
  return { verdict, overridden: chosenMode !== undefined };
}

/** Does the document itself show Czech tax? Lines win when there are
 *  any, the same way the totals do. */
function documentCarriesVat(form: Draft): boolean {
  const lines = form.lines ?? [];
  if (lines.length > 0) {
    return lines.some((line) => Number(line.vatRate || 0) > 0);
  }
  return Number(form.vatTotal ?? 0) > 0;
}

function totalsOf(
  lines: DraftLine[],
  reverseCharge: boolean,
): { subtotal: number; vat: number; total: number } {
  let subtotal = 0;
  let vat = 0;
  for (const line of lines) {
    const base = Number(line.quantity || 0) * Number(line.unitPrice || 0);
    subtotal += base;
    vat += (base * Number(line.vatRate || 0)) / 100;
  }
  const round = (n: number) => Math.round(n * 100) / 100;
  return {
    subtotal: round(subtotal),
    vat: round(vat),
    // Under reverse charge the supplier billed the base only — the tax
    // goes to the tax office, not into what we owe them.
    total: round(reverseCharge ? subtotal : subtotal + vat),
  };
}

/**
 * The CNB fixing for the taxable date. Asked for only when it can
 * matter (a foreign currency and a date), and it never fills the field
 * behind the person's back — it offers what it found.
 */
function useExchangeRate(currency: string | undefined, on: string | undefined) {
  const [value, setValue] = useState<number | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "missing">("idle");

  useEffect(() => {
    const code = (currency ?? "CZK").toUpperCase();
    if (code === "CZK" || !on) {
      setValue(null);
      setState("idle");
      return;
    }
    let live = true;
    setState("loading");
    api
      .get<{ rate: number | null }>(
        `/api/accounting/exchange-rate?currency=${encodeURIComponent(code)}&on=${encodeURIComponent(on)}`,
      )
      .then((res) => {
        if (!live) return;
        setValue(res.rate);
        setState(res.rate === null ? "missing" : "idle");
      })
      .catch(() => live && setState("missing"));
    return () => {
      live = false;
    };
  }, [currency, on]);

  return { value, state };
}

/**
 * The document's own lines.
 *
 * Hidden until asked for: most receipts are a single amount and a table
 * of one row is noise. Once there are lines they are the truth and the
 * totals underneath follow from them — two places to type the same
 * number is how a document ends up disagreeing with itself.
 */
/**
 * What the rules make of this document, and the two facts they need
 * that nothing can read off the paper: what kind of supply it is, and
 * whether a Czech supplier moved the tax to us.
 *
 * It shows the verdict with its section of the Act rather than a
 * yes/no, because the accountant is the one answering for it — and it
 * says plainly when it doesn't know instead of picking something.
 */
function VatPanel({
  form,
  verdict,
  overridden,
  reverseCharge,
  supplyCode,
  supplyCodes,
  onPick,
}: {
  form: Draft;
  verdict: Verdict | null;
  overridden: boolean;
  reverseCharge: boolean;
  supplyCode: ReturnType<typeof supplyCodeField>;
  supplyCodes: Record<string, string> | null;
  onPick: (patch: Partial<Draft>) => void;
}) {
  const { t } = useTranslation();
  const domestic = !isForeign(form.supplierCountry, form.supplierVatNo);

  return (
    <div className="space-y-2 rounded-md border p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">{t("accInbox.vat.thisIs")}</span>
        {(["service", "goods", "goods_with_installation", "construction", "import"] as const).map(
          (type) => (
            <button
              key={type}
              type="button"
              onClick={() => onPick({ supplyType: type })}
              className={cn(
                "rounded border px-2 py-0.5",
                (form.supplyType ?? "service") === type
                  ? "border-primary bg-primary/10 text-primary"
                  : "text-muted-foreground",
              )}
            >
              {t(`accInbox.supplyType.${type}`)}
            </button>
          ),
        )}
      </div>

      {domestic && (
        <label className="flex items-center gap-2 text-muted-foreground">
          <input
            type="checkbox"
            checked={form.domesticReverseCharge === true}
            onChange={(e) => onPick({ domesticReverseCharge: e.target.checked })}
            className="h-3.5 w-3.5"
          />
          {t("accInbox.vat.domesticReverseCharge")}
        </label>
      )}

      {supplyCode.visible && (
        <label className="flex items-center gap-2 text-muted-foreground">
          {t("accInbox.vat.supplyCode")}
          <select
            required
            value={supplyCode.value}
            onChange={(e) => onPick({ vatSupplyCode: e.target.value || undefined })}
            className="rounded border bg-background px-2 py-0.5 text-foreground"
          >
            <option value="">{t("accInbox.vat.supplyCodeChoose")}</option>
            {Object.entries(supplyCodes ?? {}).map(([code, label]) => (
              <option key={code} value={code}>
                {code} – {label}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="flex items-center gap-2 text-muted-foreground">
        <input
          type="checkbox"
          checked={form.vatDeductible === false}
          onChange={(e) => onPick({ vatDeductible: !e.target.checked })}
          className="h-3.5 w-3.5"
        />
        {t("accInbox.vat.notDeductible")}
      </label>

      {verdict && (
        <div
          className={cn(
            "rounded-md border-l-2 pl-2",
            verdict.needsReview ? "border-amber-500" : "border-emerald-500",
          )}
        >
          <p className="font-medium">
            {verdict.selfAssess ? t("accInbox.vat.weAssess") : t("accInbox.vat.supplierAccounts")}
            {verdict.law && <span className="text-muted-foreground"> · {verdict.law}</span>}
            {verdict.needsReview && (
              <span className="text-amber-700">{t("accInbox.vat.needsLook")}</span>
            )}
          </p>
          <p className="mt-0.5 text-muted-foreground">{verdict.reason}</p>
          {(verdict.returnLines.tax.length > 0 || verdict.controlSection) && (
            <p className="mt-0.5 text-muted-foreground">
              {t("accInbox.vat.returnLabel")}{" "}
              {verdict.returnLines.tax.length > 0
                ? t("accInbox.vat.returnLines", { lines: verdict.returnLines.tax.join("/") })
                : t("accInbox.vat.returnDeductionOnly")}
              {verdict.returnLines.deduction.length > 0 &&
                t("accInbox.vat.returnDeduction", {
                  lines: verdict.returnLines.deduction.join("/"),
                })}
              {verdict.controlSection &&
                t("accInbox.vat.controlStatement", { section: verdict.controlSection })}
            </p>
          )}
        </div>
      )}

      <p className="text-muted-foreground">
        {overridden ? (
          <>
            {reverseCharge
              ? t("accInbox.vat.overriddenSelf")
              : t("accInbox.vat.overriddenSupplier")}{" "}
            <button
              type="button"
              className="underline"
              onClick={() => onPick({ vatMode: undefined })}
            >
              {t("accInbox.vat.useRules")}
            </button>
          </>
        ) : (
          <>
            {t("accInbox.vat.disagree")}{" "}
            <button
              type="button"
              className="underline"
              onClick={() => onPick({ vatMode: reverseCharge ? "standard" : "reverse_charge" })}
            >
              {reverseCharge ? t("accInbox.vat.switchToSupplier") : t("accInbox.vat.switchToUs")}
            </button>
          </>
        )}
      </p>
    </div>
  );
}

function LineEditor({
  lines,
  reverseCharge,
  currency,
  onChange,
  onSeed,
}: {
  lines: DraftLine[];
  reverseCharge: boolean;
  currency: string;
  onChange: (lines: DraftLine[]) => void;
  onSeed: () => void;
}) {
  const { t } = useTranslation();
  const totals = totalsOf(lines, reverseCharge);
  const money = (n: number) => formatCurrency(n, currency.toUpperCase());

  if (lines.length === 0) {
    return (
      <button type="button" className="text-sm underline" onClick={onSeed}>
        {t("accInbox.lines.breakIntoLines")}
      </button>
    );
  }

  const patch = (index: number, key: keyof DraftLine, value: string) =>
    onChange(lines.map((line, i) => (i === index ? { ...line, [key]: value } : line)));

  return (
    <div className="space-y-2">
      <div className="space-y-2">
        {lines.map((line, index) => (
          <div
            key={`line-${index}-${line.description}`}
            className="space-y-1 rounded-md border p-2"
          >
            <input
              value={line.description}
              onChange={(e) => patch(index, "description", e.target.value)}
              placeholder={t("accInbox.lines.description")}
              className={INPUT}
            />
            <div className="grid grid-cols-5 gap-2">
              <input
                value={line.quantity}
                onChange={(e) => patch(index, "quantity", e.target.value)}
                placeholder={t("accInbox.lines.qty")}
                className={INPUT}
              />
              <input
                value={line.unitName}
                onChange={(e) => patch(index, "unitName", e.target.value)}
                placeholder={t("accInbox.lines.unit")}
                className={INPUT}
              />
              <input
                value={line.unitPrice}
                onChange={(e) => patch(index, "unitPrice", e.target.value)}
                placeholder={t("accInbox.lines.unitPrice")}
                className={`${INPUT} col-span-2`}
              />
              <input
                value={line.vatRate}
                onChange={(e) => patch(index, "vatRate", e.target.value)}
                placeholder={t("accInbox.lines.vatPct")}
                className={INPUT}
              />
            </div>
            <button
              type="button"
              className="text-muted-foreground text-xs underline"
              onClick={() => onChange(lines.filter((_, i) => i !== index))}
            >
              {t("accInbox.lines.removeLine")}
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="text-sm underline"
        onClick={() =>
          onChange([
            ...lines,
            { description: "", quantity: "1", unitName: "", unitPrice: "0", vatRate: "21" },
          ])
        }
      >
        {t("accInbox.lines.addLine")}
      </button>

      <table className="ml-auto text-sm">
        <tbody>
          <tr>
            <td className="pr-6 text-muted-foreground">{t("accInbox.lines.base")}</td>
            <td className="text-right tabular-nums">{money(totals.subtotal)}</td>
          </tr>
          <tr>
            <td className="pr-6 text-muted-foreground">
              {reverseCharge ? t("accInbox.lines.vatWeAssess") : t("accInbox.lines.vat")}
            </td>
            <td className="text-right tabular-nums">{money(totals.vat)}</td>
          </tr>
          <tr className="border-t">
            <td className="pr-6 font-medium">
              {reverseCharge ? t("accInbox.lines.payable") : t("accInbox.lines.total")}
            </td>
            <td className="text-right font-semibold tabular-nums">{money(totals.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SupplierPicker({
  contacts,
  form,
  read,
  onPick,
}: {
  contacts: ContactOption[];
  form: Draft;
  /** The name the model read, kept so picking a contact is reversible. */
  read: string | null;
  onPick: (patch: Partial<Draft>) => void;
}) {
  const router = useRouter();
  const { t } = useTranslation();
  const add = useApiMutation(
    () =>
      api.post<{ id: string; name: string }>("/api/accounting/contacts", {
        name: form.supplierName?.trim() ?? "",
        role: "supplier",
        registrationNo: form.supplierRegistrationNo ?? null,
        vatNo: form.supplierVatNo ?? null,
        ...(form.supplierCountry ? { country: form.supplierCountry } : {}),
        ...(form.currency ? { currency: form.currency } : {}),
      }),
    {
      onSuccess: (contact) => {
        onPick({ contactId: contact.id, supplierName: contact.name });
        router.invalidate();
      },
      fallbackMessage: t("accInbox.supplier.addFailed"),
    },
  );
  const options = [
    { value: NOT_IN_BOOK, label: t("accInbox.supplier.notInBook") },
    ...contacts
      .filter((contact) => contact.role !== "customer")
      .map((contact) => ({
        value: contact.id,
        label: contact.name,
        ...(contact.registrationNo ? { hint: contact.registrationNo } : {}),
      })),
    // A partner we also sell to is still a possible supplier; it just
    // isn't the first thing to offer.
    ...contacts
      .filter((contact) => contact.role === "customer")
      .map((contact) => ({ value: contact.id, label: contact.name })),
  ];

  return (
    <div className="space-y-2 md:col-span-3">
      <span className="text-muted-foreground text-xs">{t("accInbox.supplier.label")}</span>
      <Combobox
        options={options}
        value={form.contactId ?? NOT_IN_BOOK}
        aria-label={t("accInbox.supplier.label")}
        placeholder={t("accInbox.supplier.pick")}
        onChange={(value) => {
          if (value === NOT_IN_BOOK) {
            onPick({
              contactId: undefined,
              supplierName: read ?? form.supplierName ?? "",
            });
            return;
          }
          const contact = contacts.find((c) => c.id === value);
          if (!contact) return;
          onPick({
            contactId: contact.id,
            supplierName: contact.name,
            supplierRegistrationNo: contact.registrationNo ?? undefined,
            supplierVatNo: contact.vatNo ?? undefined,
            currency: form.currency ?? contact.currency,
          });
        }}
      />
      {!form.contactId && (
        <>
          <label className="block text-sm">
            <span className="text-muted-foreground text-xs">
              {t("accInbox.supplier.nameOnDocument")}
            </span>
            <input
              value={form.supplierName ?? ""}
              onChange={(e) => onPick({ supplierName: e.target.value })}
              required
              className={INPUT}
            />
          </label>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-muted-foreground">{t("accInbox.supplier.notInBookHint")}</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!form.supplierName?.trim() || add.pending}
              onClick={() => add.mutate()}
            >
              {add.pending ? t("accInbox.supplier.adding") : t("accInbox.supplier.addNow")}
            </Button>
            {add.error && <span className="text-destructive">{add.error}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="text-muted-foreground text-xs">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </div>
      {type === "date" ? (
        <DatePicker
          aria-label={label}
          {...(required ? { required } : {})}
          value={value ?? ""}
          onChange={onChange}
        />
      ) : (
        <input
          type={type}
          required={required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={INPUT}
        />
      )}
    </div>
  );
}

/** Reads every waiting document again, after the reading itself improved. */
function RereadAll({ count, onDone }: { count: number; onDone: () => void }) {
  const { t } = useTranslation();
  const [left, setLeft] = useState<number | null>(null);
  const reading = left !== null;
  const reread = useApiMutation(
    () => api.post<{ queued: number }>("/api/accounting/inbox/reread-all", {}),
    {
      onSuccess: (res) => setLeft(res.queued),
      fallbackMessage: t("accInbox.rereadAll.queueFailed"),
    },
  );

  // Poll the queue while documents are still being read, then reload the list.
  useEffect(() => {
    if (!reading) return;
    const timer = setInterval(async () => {
      try {
        const { total } = await api.get<{ total: number }>(
          "/api/accounting/inbox?status=pending&pageSize=1",
        );
        setLeft(total);
        if (total === 0) {
          clearInterval(timer);
          setLeft(null);
          onDone();
        }
      } catch {
        // A missed poll is retried on the next tick.
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [reading, onDone]);

  if (count === 0 && left === null) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Button
        variant="outline"
        size="sm"
        disabled={reread.pending || left !== null}
        onClick={() => {
          if (window.confirm(t("accInbox.rereadAll.confirm", { n: count }))) {
            reread.mutate();
          }
        }}
      >
        {reread.pending
          ? t("accInbox.rereadAll.queueing")
          : left !== null
            ? t("accInbox.rereadAll.reading", { left })
            : t("accInbox.rereadAll.button")}
      </Button>
      <span className="text-muted-foreground text-xs">
        {left !== null ? t("accInbox.rereadAll.reloads") : t("accInbox.rereadAll.hint")}
      </span>
      {reread.error && <span className="text-destructive text-xs">{reread.error}</span>}
    </div>
  );
}

/** Documents Hub put aside as the same one it already has; one click brings one back. */
function SetAsideDuplicates({ scans, onDone }: { scans: Scan[]; onDone: () => void }) {
  const { t } = useTranslation();
  const restore = useApiMutation(
    (id: string) => api.post(`/api/accounting/inbox/${id}/restore`, {}),
    { onSuccess: onDone, fallbackMessage: t("accInbox.duplicates.restoreFailed") },
  );
  if (scans.length === 0) return null;
  return (
    <details className="rounded-md border p-3 text-sm">
      <summary className="cursor-pointer">
        {t("accInbox.duplicates.summary", { n: scans.length })}
      </summary>
      <ul className="mt-2 space-y-1">
        {scans.map((scan) => (
          <li key={scan.id} className="flex flex-wrap items-center gap-2">
            <span>
              {scan.extraction?.summary ?? scan.note ?? t("accInbox.duplicates.document")}
            </span>
            <span className="text-muted-foreground text-xs">
              {(scan as Scan & { discardReason?: string | null }).discardReason}
            </span>
            <button
              type="button"
              className="text-muted-foreground text-xs underline"
              disabled={restore.pending}
              onClick={() => restore.mutate(scan.id)}
            >
              {t("accInbox.duplicates.bringBack")}
            </button>
          </li>
        ))}
      </ul>
      {restore.error && <p className="text-destructive text-xs">{restore.error}</p>}
    </details>
  );
}
